import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import Link from 'next/link';
import type { ConversationsState } from '../../../../lib/conversations';
import { useConversationActions, CONVERSATIONS_PAGE, type ConversationActionsFlow } from '../../../../lib/use-conversation-actions';
import { ConversationRowMenu } from '../../conversations/ConversationRowMenu';
import { DeleteConversationConfirm } from '../../conversations/DeleteConversationConfirm';
import { RenameConversationDialog } from '../../conversations/RenameConversationDialog';
import { HistoryIcon, PinIcon } from '../components/icons';
import { useRecentChats, type ChatSection } from '../hooks/use-recent-chats';
import { RECENT_CHATS_DEFAULT_OPEN } from '../shared';
import styles from './MobileSidebar.module.css';
import rowStyles from '../components/ConversationRow.module.css';

// The mobile drawer's conversation group — the same threads the wide rail lists (useRecentChats), as the
// design's disclosure row with the chevron opposite. Open by default, same as the wide rail: the recent
// threads are the drawer's main content.
//
// ONE group, used twice — Pinned Chats then Chat History — for the same reason the wide rail does it: both
// are the same control over the same rows, differing only in which conversations they take, their header
// word and their glyph.
function MobileChatGroup({
  state,
  selectedId,
  section,
  icon,
  flow,
}: {
  state: ConversationsState;
  selectedId: string | null;
  section: ChatSection;
  icon: ReactNode;
  flow: ConversationActionsFlow;
}) {
  const { chats, open, toggle, label } = useRecentChats({ state, selectedId, defaultOpen: RECENT_CHATS_DEFAULT_OPEN, section });
  if (chats.length === 0) return null;

  return (
    <section className={`${styles.group} ${styles.groupRecent}`}>
      <button type="button" onClick={toggle} aria-expanded={open} className={`${styles.row} ${styles.recentHeader}`}>
        <span aria-hidden className={styles.rowIcon}>{icon}</span>
        <span className={styles.rowLabel}>{label}</span>
        <ChevronDown aria-hidden size={24} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>
      {open && (
        <div className={styles.chatList}>
          {chats.map((chat) => (
            <div key={chat.conv.id} className={rowStyles.row}>
              <Link
                href={chat.route}
                as={chat.path}
                shallow
                onClick={chat.onClick}
                aria-current={chat.isSelected ? 'page' : undefined}
                className={`${styles.chatRow} ${rowStyles.link}`}
              >
                <span className={styles.chatTitle}>{chat.title}</span>
                {chat.channelBadge && <span className={styles.chatChannel}>{chat.channelBadge}</span>}
              </Link>
              <span className={rowStyles.menu}>
                <ConversationRowMenu conv={chat.conv} title={chat.title} flow={flow} />
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/// The drawer's two conversation groups: Pinned Chats above Chat History.
///
/// The delete confirm is mounted ONCE here rather than inside each group — two mounted dialogs sharing one
/// flow would both open on the same request.
export function MobileRecentChats({
  state,
  selectedId,
}: {
  state: ConversationsState;
  selectedId: string | null;
}) {
  const flow = useConversationActions(CONVERSATIONS_PAGE);
  return (
    <>
      <MobileChatGroup state={state} selectedId={selectedId} section="pinned" icon={<PinIcon />} flow={flow} />
      <MobileChatGroup state={state} selectedId={selectedId} section="recent" icon={<HistoryIcon />} flow={flow} />
      <DeleteConversationConfirm flow={flow} />
      <RenameConversationDialog flow={flow} />
    </>
  );
}
